import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useNavigation } from '@/contexts/NavigationContext';
import { isSubscriptionActive, isSubscriptionExpiredOrCancelled } from '@/lib/subscription-utils';
import {
  Bell,
  Bot,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Layers,
  PanelLeft,
  Zap,
  IndianRupee,
  ShieldCheck,
  Shield,
  Send,
  BarChart2,
  ScrollText,
  FileText,
  Briefcase,
  Megaphone,
  MessageSquare,
  Minus,
  Plus,
  X,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
  SidebarTrigger,
  SidebarHeader,
  SidebarFooter,
} from '@/components/ui/sidebar';
import { SidebarWorkspaceSwitcher } from '@/components/layout/SidebarWorkspaceSwitcher';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/components/AuthProvider';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { ALWAYS_ENABLED_FEATURES } from '@/hooks/useFeatureLimits';
import { useOrganizationEntitlements } from '@/hooks/useFeatureLimits';
import { useEntitlementsSettled } from '@/hooks/useEntitlementsSettled';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { BusinessAccountContext } from '@/contexts/BusinessAccountContext';
import { useContext } from 'react';
import { cn } from '@/lib/utils';
import { useIsCardShell } from './cardShell/shellVariant';
import { NavIcon, SubNavLogo } from './NavIcon';
import { PlanCreditsBar } from './PlanCreditsBar';
import { navIcon, navRailRow, navRow, navSubDot, navSubRow, navToggle } from './sidebarNavStyles';
import { HeaderBusinessSelector } from './HeaderBusinessSelector';
import { isSectionGranted } from '@/lib/menuPermissions.core';
import { isFeatureEnabledWithFallback } from '@/lib/featureEntitlements';

import home_icon from '/assets/new-sidebar-icons/home-overview.svg';
import overview_svg from '/assets/new-sidebar-icons/Dashboard.svg';
import allMessages_svg from '/assets/new-sidebar-icons/All-Messages.svg';
import autoReplies_svg from '/assets/new-sidebar-icons/Auto-Replies-Bots.svg';
import campaigns_svg from '/assets/new-sidebar-icons/Campaigns.svg';
import appointments_svg from '/assets/new-sidebar-icons/Appointments.svg';
import onlineStore_svg from '/assets/new-sidebar-icons/Online-Store.svg';
import crm_svg from '/assets/new-sidebar-icons/CRM.svg';
import dataStore_svg from '/assets/new-sidebar-icons/Database.svg';
import analytics_svg from '/assets/new-sidebar-icons/Analytics.svg';
import accountSetup from '/assets/new-sidebar-icons/Account-Setup.svg';
import support_svg from '/assets/new-sidebar-icons/Support.svg';
import ai_svg from '/assets/new-sidebar-icons/ai.svg';
import instagram_svg from '/assets/new-sidebar-icons/Instagram.svg';
import { AD_EXPERIMENTS_ENABLED, COMPETITOR_ADS_ENABLED } from "@/lib/deploymentEnv";

const fig_bubbleChat = '/figma-sidebar/bubble-chat.svg';
const fig_campaigns = '/figma-sidebar/campaigns.svg';
const fig_crm = '/figma-sidebar/crm.svg';
const fig_automations = '/figma-sidebar/automations.svg';
const fig_appointments = '/figma-sidebar/appointments.svg';
const fig_shoppingCart = '/figma-sidebar/shopping-cart-02.svg';
const fig_commerce = '/figma-sidebar/commerce.svg';
const fig_dataStore = '/figma-sidebar/data-store.svg';
const fig_reports = '/figma-sidebar/reports-analytics.svg';
const fig_ads = '/figma-sidebar/ads.svg';
const fig_ai = '/figma-sidebar/ai.svg';
const fig_generalSetting = '/figma-sidebar/general-setting.svg';
const fig_menuSquare = '/figma-sidebar/menu-square.svg';
const fig_info = '/figma-sidebar/info.svg';
const fig_socialMediaPosts = '/figma-sidebar/social-media-posts.svg';
const icon_instagram = '/icons/instagram.svg';
const logo_whatsapp = '/assets/social-media/whatsapp-icon-logo-svgrepo-com.svg';
const logo_instagram = '/assets/social-media/instagram-icon.svg';
const logo_facebook = '/assets/social-media/facebook-icon.svg';

import { hasAnyEntitledIntegration } from '@/lib/integrationEntitlements';

const we_next_logo = '/assets/wenext_logo_dark.png';
const small_we_next_logo = '/assets/loading-icons/we-next-icon.jpg';

const dashboard_icon = '/assets/sidebar-icons/superadmin-icons/dashboard-icon.svg';
const dashboard_filled_icon = '/assets/sidebar-icons/superadmin-icons/dashboard-filled-icon.svg';
const clients_icon = '/assets/sidebar-icons/superadmin-icons/clients-icon.svg';
const clients_filled_icon = '/assets/sidebar-icons/superadmin-icons/clients-filled-icon.svg';
const agencies_icon = '/assets/sidebar-icons/superadmin-icons/agencies-icon.svg';
const agencies_filled_icon = '/assets/sidebar-icons/superadmin-icons/agencies-filled-icon.svg';
const subscriptions_icon = '/assets/sidebar-icons/superadmin-icons/subscriptions-icon.svg';
const subscriptions_filled_icon = '/assets/sidebar-icons/superadmin-icons/subscriptions-filled-icon.svg';
const organizations_icon = '/assets/sidebar-icons/superadmin-icons/agencies-icon.svg';
const organizations_filled_icon = '/assets/sidebar-icons/superadmin-icons/agencies-filled-icon.svg';
const compensations_icon = '/assets/sidebar-icons/superadmin-icons/subscriptions-icon.svg';
const compensations_filled_icon = '/assets/sidebar-icons/superadmin-icons/subscriptions-filled-icon.svg';
const settings_icon = '/assets/settings.svg';
const settings_filled_icon = '/assets/settings-filled.svg';
const templates_icon = '/assets/templates.svg';
const templates_filled_icon = '/assets/templates-filled.svg';

export const ROUTE_ROLE_RESTRICTIONS: Record<string, string[]> = {
  '/home/audit-log': ['ADMIN'],
  '/home/billing': ['ADMIN'],
  '/home/integrations': ['ADMIN'],
  '/home/workspace-settings': ['ADMIN'],
  '/home/business-accounts': ['ADMIN'],
  '/home/social-accounts': ['ADMIN'],
  '/home/team-management': ['ADMIN'],
  '/home/short-links': ['ADMIN'],
  '/home/plans': ['ADMIN'],
};

export const primaryMenuItems = [
  {
    id: 'channels',
    title: 'Inbox',
    icon: fig_bubbleChat,
    secondaryItems: [
      { title: 'WhatsApp', url: '/home/channels/whatsapp', featureKey: 'inbox', logo: logo_whatsapp },
      { title: 'Instagram', url: '/home/channels/instagram', featureKey: 'inbox', logo: logo_instagram },
      { title: 'Facebook', url: '/home/channels/facebook', featureKey: 'inbox', logo: logo_facebook },
      { title: 'AI Chats', url: '/home/channels/ai-agents', featureKey: 'inbox', logoIcon: Bot, logoIconClassName: 'text-purple-600' },
    ],
  },
  {
    id: 'new-campaigns',
    title: 'Campaigns',
    icon: fig_campaigns,
    secondaryItems: [
      { title: 'Campaigns', url: '/home/new-campaigns', featureKey: 'campaigns' },
    ],
  },
  {
    id: 'customers',
    title: 'CRM',
    icon: fig_crm,
    secondaryItems: [
      { title: 'CRM', url: '/home/boards/leads', featureKey: 'contacts' },
    ],
  },
  {
    id: 'automation',
    title: 'Automations',
    icon: fig_automations,
    secondaryItems: [
      { title: 'Automations', url: '/home/automation', featureKey: 'automations' },
      { title: 'Forms', url: '/home/flows', featureKey: 'whatsapp_flows' },
    ],
  },
  {
    id: 'social-media',
    title: 'Social Media',
    icon: fig_socialMediaPosts,
    secondaryItems: [
      { title: 'Social Media', url: '/home/social-media/posts', featureKey: 'social_media_posts' },
    ],
  },
  {
    id: 'instagram',
    title: 'Instagram',
    icon: icon_instagram,
    secondaryItems: [
      { title: 'Instagram Posts', url: '/home/instagram/posts', featureKey: 'instagram_automation' },
      { title: 'Instagram Insights', url: '/home/instagram/insights', featureKey: 'instagram_automation' },
      { title: 'Instagram Automations', url: '/home/automation/instagram', featureKey: 'instagram_automation' },
      { title: 'Automation Leads', url: '/home/instagram/automation/leads', featureKey: 'instagram_automation' },
      { title: 'Instagram Orders', url: '/home/instagram/orders', featureKey: 'instagram_automation' },
    ],
  },
  {
    id: 'scheduling',
    title: 'Appointments',
    icon: fig_appointments,
    secondaryItems: [
      { title: 'Overview', url: '/home/scheduling-overview', featureKey: 'appointments' },
      { title: 'Scheduling', url: '/home/scheduling', featureKey: 'scheduling' },
      { title: 'Meetings', url: '/home/meeting-links', featureKey: 'meeting_links' },
      { title: 'Contacts', url: '/home/scheduling-contacts', featureKey: 'appointments' },
      { title: 'Availability Slots', url: '/home/time-slots', featureKey: 'time_slots' },
      { title: 'Calendar Integrations', url: '/home/scheduling-integrations', featureKey: 'appointments' },
      { title: 'WhatsApp Reminders', url: '/home/whatsapp-reminders', featureKey: 'appointments' },
    ],
  },
  {
    id: 'appointments-v2',
    title: 'Appointments',
    icon: fig_appointments,
    secondaryItems: [
      { title: 'Appointments', url: '/home/appointments-v2', featureKey: 'appointments' },
    ],
  },
  {
    id: 'store',
    title: 'Commerce',
    icon: fig_shoppingCart,
    secondaryItems: [
      { title: 'Overview', url: '/home/commerce/dashboard', featureKey: 'commerce' }, // Using 'commerce' key as dashboard is part of it
      { title: 'Product Catalog', url: '/home/commerce/products', featureKey: 'products' },
      { title: 'Orders', url: '/home/orders', featureKey: 'orders' },
      { title: 'Abandoned Carts', url: '/home/abonded-carts', featureKey: 'abonded_carts' },
      { title: 'Payments', url: '/home/payments', featureKey: 'payments' },
      { title: 'Invoices', url: '/home/invoices', featureKey: 'invoices' },
    ],
  },
  {
    id: 'commerce-v2',
    title: 'Commerce',
    icon: fig_shoppingCart,
    secondaryItems: [
      { title: 'Commerce', url: '/home/commerce/v2', featureKey: 'commerce' },
    ],
  },
  {
    id: 'data-store',
    title: 'Data Store',
    icon: fig_dataStore,
    secondaryItems: [
      { title: 'Data Store', url: '/home/data-stores', featureKey: 'data_store' },
    ],
  },
  {
    id: 'ads',
    title: 'Ads',
    icon: fig_ads,
    featureKey: 'ads',
    secondaryItems: [
      { title: 'Meta Ads', url: '/home/ads', featureKey: 'meta_ads' },
      ...(AD_EXPERIMENTS_ENABLED
        ? [{ title: 'Experiments', url: '/home/ads/experiments', featureKey: 'meta_ads' }]
        : []),
      ...(COMPETITOR_ADS_ENABLED
        ? [{ title: 'Competitor Ads', url: '/home/competitor-ads', featureKey: null }]
        : []),
      { title: 'LinkedIn Ads', url: '/home/linkedin-ads', featureKey: 'linkedin_ads' },
      { title: 'TikTok Ads', url: '/home/tiktok-ads', featureKey: 'tiktok_ads' },
      { title: 'Google Ads', url: '/home/google-ads', featureKey: 'google_ads' },
    ],
  },
  {
    id: 'reports',
    title: 'Reports',
    icon: fig_reports,
    secondaryItems: [
      { title: 'Reports', url: '/home/analytics', featureKey: 'analytics' },
      { title: 'Sales & Conversions', url: '/home/sales-and-conversions', featureKey: 'sales_and_conversions' },
    ],
  },
  {
    id: 'ai-tools',
    title: 'AI Agents',
    icon: fig_ai,
    secondaryItems: [
      { title: 'AI Agents', url: '/home/ai-agents', featureKey: 'ai_generation' },
      { title: 'Live Chat', url: '/home/ai-agents?type=general', featureKey: 'ai_generation' },
      { title: 'WhatsApp AI Agent', url: '/home/ai-agents?type=whatsapp', featureKey: 'ai_generation' },
      { title: 'Voice Agents', url: '/home/ai-agents?type=voice', featureKey: 'ai_calling' },
    ],
  },
  {
    id: 'petpooja',
    title: 'Petpooja',
    icon: fig_commerce,
    secondaryItems: [
      { title: 'Overview & Sync', url: '/home/petpooja', featureKey: 'petpooja' },
      { title: 'Order History', url: '/home/petpooja/orders', featureKey: 'petpooja' },
    ],
  },
  {
    id: 'settings',
    title: 'Account Settings',
    icon: fig_generalSetting,
    secondaryItems: [
      { title: 'General Settings', url: '/home/account-settings', featureKey: null },
      { title: 'Team Chat', url: '/home/team-chat', featureKey: null },
      { title: 'Team Management', url: '/home/team-management', featureKey: 'team_management' },
      { title: 'Audit Log', url: '/home/audit-log', featureKey: null },
      { title: 'Integrations', url: '/home/integrations', featureKey: 'integrations' },
      { title: 'Billing', url: '/home/billing', featureKey: null },
      { title: 'Plans & Subscriptions', url: '/home/plans', featureKey: null },
      { title: 'Business Accounts', url: '/home/business-accounts', featureKey: 'business_accounts' },
    ],
  },
  {
    id: 'help',
    title: 'Support',
    icon: fig_info,
    secondaryItems: [
      { title: 'FAQs', url: '/home/help-articles', featureKey: 'help_articles' },
    ],
  },
];

const HIDDEN_PRIMARY_MENU_IDS: string[] = [
  'instagram',
  'settings',
  'help',
  'reports',
  'data-store',
  'scheduling',
  'store',
];

export const standaloneNavItems = [
  { title: 'Overview', url: '/home', icon: fig_menuSquare, featureKey: null },
];

const mainNavItems = [
  { title: 'Overview', url: '/home', icon: home_icon, featureKey: null },
  { title: 'Inbox', url: '/home/inbox', icon: allMessages_svg, featureKey: 'inbox' },
  { title: 'New Inbox', url: '/home/new-inbox', icon: allMessages_svg, featureKey: 'inbox' },
  { title: 'Contacts', url: '/home/contacts', featureKey: 'contacts' },
  { title: 'Message Templates', url: '/home/templates', featureKey: 'templates' },
  { title: 'WhatsApp Campaigns', url: '/home/campaigns', featureKey: 'campaigns' },
  { title: 'Post Scheduling', url: '/home/scheduling-posts', featureKey: 'scheduling_posts' },
  { title: 'Social Media Posts', url: '/home/social-media/posts', featureKey: 'social_media_posts' },
  { title: 'Forms', url: '/home/flows', featureKey: 'whatsapp_flows' },
  { title: 'Form Submissions', url: '/home/form-data', featureKey: 'whatsapp_flows' },
  { title: 'Google Sheets Data', url: '/home/google-sheets-data' },
  { title: 'Customer Boards', url: '/home/boards', featureKey: 'contacts' },
  { title: 'Scheduling', url: '/home/scheduling', featureKey: 'scheduling' },
  { title: 'Availability Slots', url: '/home/time-slots', featureKey: 'time_slots' },
  { title: 'Meetings', url: '/home/meeting-links', featureKey: 'meeting_links' },
  { title: 'Scheduling Contacts', url: '/home/scheduling-contacts', featureKey: 'appointments' },
  { title: 'Calendar Integrations', url: '/home/scheduling-integrations', featureKey: 'integrations' },
  { title: 'WhatsApp Reminders', url: '/home/scheduling/whatsapp-reminders', featureKey: 'whatsapp_reminders' },
  { title: 'Automations', url: '/home/automation', featureKey: 'automations' },
  { title: 'Instagram Posts', url: '/home/instagram/posts', featureKey: 'instagram_automation' },
  { title: 'Instagram Insights', url: '/home/instagram/insights', featureKey: 'instagram_automation' },
  { title: 'Instagram Automations', url: '/home/automation/instagram', featureKey: 'instagram_automation' },
  { title: 'Instagram Automation Leads', url: '/home/instagram/automation/leads', featureKey: 'instagram_automation' },
  { title: 'Instagram Orders', url: '/home/instagram/orders', featureKey: 'instagram_automation' },
  { title: 'Orders', url: '/home/orders', featureKey: 'orders' },
  { title: 'Product Catalog', url: '/home/products', featureKey: 'products' },
  { title: 'Abandoned Carts', url: '/home/abonded-carts', featureKey: 'abonded_carts' },
  { title: 'Payments', url: '/home/payments', featureKey: 'payments' },
  { title: 'Commerce Reports', url: '/home/store-reports', featureKey: 'store_reports' },
  { title: 'Social Accounts', url: '/home/social-accounts', featureKey: 'social_media_posts' },
  { title: 'Notifications', url: '/home/notifications', icon: Bell, featureKey: null },
];

const businessNavItems = [
  { title: 'Business Accounts', url: '/home/integrations/whatsapp-business', featureKey: 'business_accounts' },
  { title: 'Call Logs', url: '/home/call-logs', featureKey: null },
  { title: 'Dashboard', url: '/home/analytics', featureKey: 'analytics' },
  { title: 'Message Reports', url: '/home/message-reports', featureKey: 'message_reports' },
  { title: 'Sales Reports', url: '/home/ads-results', featureKey: 'ads_results' },
  { title: 'Sales & Conversions', url: '/home/sales-and-conversions', featureKey: 'sales_and_conversions' },
  { title: 'Commerce', url: '/home/commerce/products', featureKey: 'commerce' },
  { title: 'Team Management', url: '/home/team-management', featureKey: 'team_management' },
  { title: 'General Settings', url: '/home/account-settings', featureKey: null },
  { title: 'Integrations', url: '/home/integrations', featureKey: 'integrations' },
  { title: 'Billing', url: '/home/billing', featureKey: 'billing' },
];

const adminNavItems = [
  { title: 'Dashboard', url: '/admin/dashboard', icon: dashboard_icon, filledIcon: dashboard_filled_icon },
  { title: 'Clients', url: '/admin/clients', icon: clients_icon, filledIcon: clients_filled_icon },
  { title: 'Internal Accounts', url: '/admin/internal-accounts', icon: Shield, filledIcon: Shield },
  { title: 'Agencies', url: '/admin/agencies', icon: agencies_icon, filledIcon: agencies_filled_icon },
  { title: 'Library', url: '/admin/library', icon: templates_icon, filledIcon: templates_filled_icon },
  { title: 'Subscriptions', url: '/admin/subscriptions', icon: subscriptions_icon, filledIcon: subscriptions_filled_icon },
  { title: 'Plans', url: '/admin/subscription-plans', icon: Layers, filledIcon: Layers },
  { title: 'Pricing', url: '/admin/pricing', icon: IndianRupee, filledIcon: IndianRupee },
  { title: 'Revenue Analytics', url: '/admin/revenue-analytics', icon: BarChart2, filledIcon: BarChart2 },
  { title: 'WeNext Billing', url: '/admin/billing/invoices', icon: FileText, filledIcon: FileText },
  { title: 'PhotonX Billing', url: '/admin/photonx', icon: Briefcase, filledIcon: Briefcase },
  { title: 'Maya Billing', url: '/admin/maya', icon: Megaphone, filledIcon: Megaphone },
  { title: 'Admin Users', url: '/admin/users', icon: ShieldCheck, filledIcon: ShieldCheck },
  { title: 'Audit Log', url: '/admin/audit-log', icon: ScrollText, filledIcon: ScrollText },
  { title: 'Messages', url: '/admin/messages', icon: MessageSquare, filledIcon: MessageSquare },
];

const agencyNavItems = [
  { title: 'Dashboard', url: '/home/agency/dashboard', icon: dashboard_icon, filledIcon: dashboard_filled_icon },
  { title: 'Organizations', url: '/home/agency/organizations', icon: organizations_icon, filledIcon: organizations_filled_icon },
  { title: 'Compensations', url: '/home/agency/compensations', icon: compensations_icon, filledIcon: compensations_filled_icon },
  { title: 'General Settings', url: '/home/agency/settings', icon: settings_icon, filledIcon: settings_filled_icon },
];

export const AppSidebar: React.FC = () => {
  const isCardShell = useIsCardShell();
  const { state, setOpenMobile, isMobile, setOpen, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { interceptedNavigate } = useNavigation();
  const collapsed = state === 'collapsed';
  const { user, signOut, profileSynced } = useAuth();
  const businessAccountContext = useContext(BusinessAccountContext);
  const businessAccountId = businessAccountContext?.selectedAccountId || null;
  const { totalUnreadConversations } = useUnreadMessages();

  const handleNavigation = (url: string) => {
    if (url.startsWith('http')) {
      window.open(url, '_blank');
    } else {
      interceptedNavigate(url);
    }
    if (isMobile) setOpenMobile(false);
  };

  const { data: entitlements, isLoading: entitlementsLoading, isError: entitlementsError } = useOrganizationEntitlements();
  const organizationIdForSettle = (user as any)?.organization_id;
  const entitlementsSettled = useEntitlementsSettled(!!organizationIdForSettle);
  const isLoadingEntitlements =
    (!entitlementsError && !entitlementsSettled) || profileSynced === false;
  const baseFeatures = entitlements?.entitlements?.features || {};
  const enabledFeatures = {
    ...baseFeatures,
    scheduling: baseFeatures.appointments ?? false,
    time_slots: baseFeatures.appointments ?? false,
    meeting_links: baseFeatures.appointments ?? false,
    whatsapp_reminders: baseFeatures.appointments ?? false,
    orders: baseFeatures.commerce ?? false,
    products: baseFeatures.commerce ?? false,
    abonded_carts: baseFeatures.commerce ?? false,
    payments: baseFeatures.commerce ?? false,
    invoices: baseFeatures.commerce ?? false,
    store_reports: baseFeatures.commerce ?? false,
    meta_ads: entitlements?.entitlements?.sub_features?.ads?.meta_ads > 0,
    linkedin_ads: entitlements?.entitlements?.sub_features?.ads?.linkedin_ads > 0,
    scheduling_posts: true,
    lead_ads_sync: true,
    whatsapp_ads: true,
    segments: true,
    chat_history: true,
    message_reports: true,
    campaign_performance: true,
    ads_results: true,
    sales_and_conversions: true,
    billing: true,
    integrations: true,
    how_to_use: true,
    whats_new: true,
    help_articles: true,
    raise_ticket: true,
    contact_support: true
  };

  const { data: currentSubscription, isLoading: isLoadingSubscription, isError: isSubscriptionError } = useCurrentSubscription();

  const hasActiveSubscription = isSubscriptionActive(currentSubscription);

  const isSubscriptionExpired = !isLoadingSubscription && !isSubscriptionError && (
    !currentSubscription ||
    isSubscriptionExpiredOrCancelled(currentSubscription)
  );


  const integrationSubFeatures = entitlements?.entitlements?.sub_features?.integrations;

  const hasEnabledIntegrationConnector = React.useMemo(
    () =>
      hasAnyEntitledIntegration({
        hasEntitlements: Boolean(entitlements) && !entitlementsError,
        features: baseFeatures,
        subFeatures: integrationSubFeatures,
        limits: entitlements?.entitlements?.limits,
      }),
    [entitlements, entitlementsError, baseFeatures, integrationSubFeatures]
  );

  const isFeatureEnabled = React.useCallback((featureKey: string | null, url?: string): boolean => {
    if (url === '/home/integrations') {
      return hasEnabledIntegrationConnector;
    }
    if (!featureKey) return true;
    if (featureKey === 'integrations') {
      return true; // Calendar Integrations et al — unchanged
    }
    if (featureKey === 'billing') {
      return true; // Always show billing
    }
    return isFeatureEnabledWithFallback(enabledFeatures, featureKey);
  }, [enabledFeatures, hasEnabledIntegrationConnector]);

  const handleNavClick = React.useCallback((e: React.MouseEvent) => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);

  const roleRaw = (user?.role || '').toString().toUpperCase();
  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;
  const isOwner = !!(user as any)?.is_organization_owner;
  const isAdmin = roleRaw === 'ADMIN' || isSuperAdmin || isOwner;
  const currentRole = isAdmin ? 'ADMIN' : (roleRaw === 'AGENT' ? 'AGENT' : (roleRaw === 'RIDER' ? 'RIDER' : 'USER'));

  const showAdminNav = isSuperAdmin;
  const showAgencyNav = isAdmin && isAgency && !isSuperAdmin;
  const showRegularNav = !showAdminNav && !showAgencyNav;

  const allowedByRole = React.useMemo(() => ({
    ADMIN: [
      '/home', '/home/inbox', '/home/new-inbox', '/home/channels/whatsapp', '/home/channels/instagram', '/home/channels/facebook', '/home/channels/ai-agents', '/home/call-logs',
      '/home/customers', '/home/leads', '/home/segments', '/home/chat-history', '/home/notes-and-tags', '/home/tickets',
      '/home/meta-ads', '/home/lead-ads-sync', '/home/templates', '/home/campaigns', '/home/new-campaigns', '/home/new-templates',
      '/home/scheduling-posts', '/home/instagram-broadcasts', '/home/social-media/posts', '/home/instagram/posts', '/home/facebook/posts', '/home/linkedin/posts', '/home/flows', '/home/orders', '/home/commerce/products',
      '/home/abonded-carts', '/home/payments', '/home/invoices', '/home/store-reports', '/home/form-data', '/home/scheduling', '/home/appointments-v2', '/home/time-slots',
      '/home/meeting-links', '/home/scheduling-contacts', '/home/scheduling/whatsapp-reminders', '/home/whatsapp-reminders', '/home/boards/leads', '/home/deals', '/home/deals/stages', '/home/automation', '/home/instagram/automation', '/home/automation/instagram',
      '/home/instagram/inbox', '/home/instagram/insights', '/home/instagram/orders', '/home/instagram/automation/create', '/home/instagram/automation/builder', '/home/instagram/automation/leads', '/home/instagram/automation/logs',
      '/home/analytics', '/home/message-reports', '/home/campaign-performance', '/home/ads-results',
      '/home/business-accounts', '/home/social-accounts', '/home/short-links', '/home/commerce/dashboard', '/home/commerce/v2', '/home/data-stores', '/home/petpooja', '/home/petpooja/orders', '/home/team-management', '/home/audit-log', '/home/account-settings', '/home/workspaces', '/home/workspace-settings', '/home/settings', '/home/integrations', '/home/scheduling-integrations', '/home/billing', '/home/plans', '/home/updates', '/home/whats-new', '/home/how-to-use', '/home/help-articles', '/home/raise-ticket', 'https://wa.me/918977232350', '/home/ai-agents', '/home/live-chat', '/home/whatsapp-ai-agent', '/home/chatbot', '/home/google-sheets-data', '/home/scheduling-overview', '/home/boards', '/home/social-media/instagram/posts', '/home/linkedin/posts', '/home/ads', '/home/ads/create', '/home/ads/setup', '/home/linkedin-ads', '/home/linkedin-ads/setup', '/home/linkedin-ads/create', '/home/linkedin-ads/analytics', '/home/linkedin-ads/ad-library', '/home/linkedin-ads/events', '/home/linkedin-ads/verified', '/home/tiktok-ads', '/home/google-ads',
      '/home/website-builder',
      '/home/social-media/youtube',
      '/home/social-media/youtube/content',
      '/home/social-media/youtube/comments',
      '/home/social-media/youtube/insights',
      '/home/social-media/youtube/automation',
      '/home/voice-agents',
      '/home/team-chat'
    ],

    USER: ['/home', '/home/inbox', '/home/new-inbox', '/home/channels/whatsapp', '/home/channels/instagram', '/home/channels/facebook', '/home/channels/ai-agents', '/home/contacts', '/home/tickets', '/home/templates', '/home/campaigns', '/home/new-campaigns', '/home/new-templates', '/home/form-data', '/home/boards', '/home/deals', '/home/deals/stages', '/home/account-settings', '/home/workspaces', '/home/settings', '/home/team-management', '/home/integrations', '/home/scheduling-integrations', '/home/billing', '/home/short-links', '/home/competitor-ads', '/home/scheduling', '/home/appointments-v2', '/home/time-slots', '/home/meeting-links', '/home/scheduling-contacts', '/home/voice-agents', '/home/ai-agents', '/home/team-chat'],

    AGENT: ['/home', '/home/inbox', '/home/new-inbox', '/home/channels/whatsapp', '/home/channels/instagram', '/home/channels/facebook', '/home/channels/ai-agents', '/home/tickets', '/home/account-settings', '/home/workspaces', '/home/settings', '/home/voice-agents', '/home/ai-agents', '/home/team-chat'],

    RIDER: ['/home', '/home/orders', '/home/petpooja/orders', '/home/account-settings', '/home/settings'],
  }), []);

  const userMenuPermissions: string[] | null | undefined = (user as any)?.menu_permissions;
  const hasCustomPermissions = !isSuperAdmin && Array.isArray(userMenuPermissions);

  const isRoleAllowedForUrl = React.useCallback((url: string): boolean => {
    const allowed = ROUTE_ROLE_RESTRICTIONS[url];
    return !allowed || allowed.includes(currentRole);
  }, [currentRole]);

  const filterSecondaryItems = React.useCallback((secondaryItems: any[]) => {
    if (isSubscriptionExpired) {
      return secondaryItems.filter(item => isFeatureEnabled(item.featureKey, item.url));
    }
    return secondaryItems.filter(item =>
      allowedByRole[currentRole].includes(item.url) && isFeatureEnabled(item.featureKey, item.url) && isRoleAllowedForUrl(item.url)
    );
  }, [currentRole, allowedByRole, isFeatureEnabled, isSubscriptionExpired, isRoleAllowedForUrl]);

  const isPrimaryGroupAllowed = React.useCallback((primaryItem: any) => {
    if (primaryItem.featureKey && !isFeatureEnabled(primaryItem.featureKey, primaryItem.url)) {
      return false;
    }
    const featureFilteredItems = primaryItem.secondaryItems.filter(
      (item: any) => isFeatureEnabled(item.featureKey, item.url)
    );
    if (featureFilteredItems.length === 0) return false;

    if (hasCustomPermissions && !isSubscriptionExpired) {
      if (primaryItem.id === 'settings' || primaryItem.id === 'help') return true;
      return isSectionGranted(primaryItem.id, userMenuPermissions!);
    }
    const filteredSubItems = filterSecondaryItems(primaryItem.secondaryItems);
    return filteredSubItems.length > 0;
  }, [hasCustomPermissions, userMenuPermissions, filterSecondaryItems, isSubscriptionExpired, isFeatureEnabled]);

  const canOpenAccountLink = React.useCallback((url: string, featureKey: string | null) => {
    const path = url.split('?')[0];
    return filterSecondaryItems([{ url: path, featureKey }]).length > 0;
  }, [filterSecondaryItems]);

  const isStandaloneItemAllowed = React.useCallback((itemUrl: string, itemId?: string) => {
    if (isSubscriptionExpired) return true;
    if (hasCustomPermissions) {
      const id = itemId || (itemUrl === '/home' ? 'overview' : itemUrl === '/home/inbox' ? 'inbox' : itemUrl);
      return userMenuPermissions!.includes(id);
    }
    return allowedByRole[currentRole].includes(itemUrl);
  }, [hasCustomPermissions, userMenuPermissions, currentRole, allowedByRole, isSubscriptionExpired]);

  const isRouteActive = React.useCallback((url: string, exact: boolean = false) => {
    const [urlPath, urlSearch] = url.split('?');
    if (urlPath === '/home') {
      return location.pathname === '/home' || location.pathname === '/home/' || location.pathname === '/home/agent';
    }
    if (urlPath === '/home/automation' && !exact) {
      if (
        location.pathname.startsWith('/home/automation/facebook') ||
        location.pathname.startsWith('/home/automation/instagram')
      ) {
        return false;
      }
    }
    const pathMatches = exact
      ? location.pathname === urlPath
      : location.pathname === urlPath || location.pathname.startsWith(urlPath + '/');
    if (!pathMatches) return false;
    if (urlSearch) {
      const wantedParams = new URLSearchParams(urlSearch);
      const wantedView = wantedParams.get('view');
      if (wantedView) {
        const actualView = new URLSearchParams(location.search).get('view');
        return actualView === wantedView;
      }
      const wantedType = wantedParams.get('type');
      if (wantedType) {
        const actualType = new URLSearchParams(location.search).get('type');
        return actualType === wantedType;
      }
    }
    if (urlPath === '/home/ai-agents' && !urlSearch) {
      const actualType = new URLSearchParams(location.search).get('type');
      if (actualType && actualType !== 'all') return false;
    }
    return true;
  }, [location.pathname, location.search]);

  const activePrimaryId = React.useMemo(() => {
    let bestId: string | null = null;
    let bestLen = -1;
    for (const item of primaryMenuItems) {
      for (const sub of item.secondaryItems) {
        if (!isRouteActive(sub.url)) continue;
        if (sub.url.length > bestLen) {
          bestLen = sub.url.length;
          bestId = item.id;
        }
      }
    }
    return bestId;
  }, [isRouteActive]);

  const isGroupActive = React.useCallback((secondaryItems: any[], groupId?: string) => {
    if (!secondaryItems.some(item => isRouteActive(item.url))) return false;
    if (groupId && activePrimaryId && groupId !== activePrimaryId) return false;
    return true;
  }, [isRouteActive, activePrimaryId]);

  const [openDropdownId, setOpenDropdownId] = React.useState<string | null>(null);
  const [openAccordion, setOpenAccordion] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (activePrimaryId) {
      setOpenAccordion(activePrimaryId);
    }
  }, [activePrimaryId]);

  const wrapWithTooltip = (navLink: React.ReactElement, title: string, forceHidden: boolean = false) => {
    if (!collapsed || isMobile) {
      return navLink;
    }
    return (
      <Tooltip delayDuration={0} open={forceHidden ? false : undefined}>
        <TooltipTrigger asChild>
          {navLink}
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={forceHidden}
          className="ml-[5px] text-[13px] font-medium"
          style={{ zIndex: 60 }}
        >
          {title}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <>
      <Sidebar collapsible="icon" className={isCardShell ? 'sm:!border-r-0' : undefined}>
        <SidebarHeader className={cn(
          'px-0',
          collapsed && !isMobile
            ? isCardShell
              ? 'relative h-[82px] py-0'
              : 'h-auto py-2'
            : 'h-14 py-0 sm:h-16 md:h-[62px]',
          isCardShell ? 'bg-transparent' : 'bg-white border-b border-slate-200'
        )}>
          <div
            className={cn(
              'flex h-full w-full',
              collapsed && !isMobile
                ? 'flex-col-reverse items-center justify-center gap-2 px-0'
                : 'items-center justify-between px-3'
            )}
          >
            {collapsed && !isMobile && isCardShell && (
              <span aria-hidden className="absolute inset-x-0 bottom-0 h-px bg-dash-border" />
            )}
            <div
              className="flex items-center cursor-pointer"
              onClick={() => {
                if (showAdminNav) {
                  interceptedNavigate('/admin/dashboard');
                } else if (showAgencyNav) {
                  interceptedNavigate('/home/agency/dashboard');
                } else {
                  interceptedNavigate('/home');
                }
              }}
              aria-label="WeNext home"
            >
              {collapsed && !isMobile ? (
                <img src={small_we_next_logo} alt="WeNext" className="w-7 h-7 rounded-[6px]" />
              ) : (
                <img src={we_next_logo} alt="WeNext Logo" className="w-auto h-[28px]" />
              )}
            </div>

            {isMobile ? (
              <button
                type="button"
                onClick={() => setOpenMobile(false)}
                className="flex items-center justify-center w-8 h-8 rounded-md text-[#6a7282] hover:bg-slate-100 hover:text-[#101828] transition-colors"
                aria-label="Close navigation menu"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            ) : collapsed && isCardShell ? null : (
              <button
                type="button"
                onClick={toggleSidebar}
                className={cn(
                  'flex shrink-0 items-center justify-center rounded-md text-[#6a7282] transition-colors hover:bg-[#F2F4F7] hover:text-[#101828]',
                  collapsed ? 'h-7 w-7' : 'h-5 w-5',
                )}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={!collapsed}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
              </button>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent className={cn(
          'flex min-h-0 flex-1 flex-col gap-0 scrollbar-hide overscroll-contain',
          isCardShell ? 'bg-transparent' : 'bg-white',
          collapsed ? 'px-2 pt-[10px] pb-2' : 'px-3 py-3'
        )}>
          {showRegularNav && (
            <SidebarWorkspaceSwitcher
              collapsed={collapsed && !isMobile}
              canOpen={canOpenAccountLink}
              onNavigate={handleNavigation}
            />
          )}
          {showRegularNav && isLoadingEntitlements && (
            <div className="flex flex-col gap-2 pt-[10px]" aria-busy="true" aria-label="Loading menu">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5 py-1.5 px-[10px]">
                  <div className="wenext-shimmer w-5 h-5 rounded-md flex-shrink-0" />
                  {!collapsed && <div className="wenext-shimmer h-4 rounded-md" style={{ width: `${55 + ((i * 13) % 35)}%` }} />}
                </div>
              ))}
            </div>
          )}
          {showRegularNav && !isLoadingEntitlements && (
            <SidebarGroup className="p-0 list-none pt-[10px]">
              <SidebarMenu className="list-none gap-0">
                {standaloneNavItems
                  .filter(item => isStandaloneItemAllowed(item.url))
                  .map((item) => {
                    const active = isRouteActive(item.url);
                    return (
                      <SidebarMenuItem key={item.title} className="list-none">
                        {wrapWithTooltip(
                          <SidebarMenuButton
                            asChild
                            isActive={active}
                            className={cn('list-none !overflow-visible', collapsed && !isMobile ? navRailRow(active) : navRow(active))}
                            onClick={() => {
                              handleNavigation(item.url);
                            }}
                          >
                            <div className={cn("flex items-center gap-3 w-full cursor-pointer relative", collapsed && !isMobile && "justify-center gap-0")}>
                              <NavIcon
                                src={item.icon}
                                active={active}
                                onBrandFill={collapsed && !isMobile}
                              />
                              <span className={cn("truncate flex-1 text-[14.5px] font-medium", collapsed && !isMobile && "sr-only")}>{item.title}</span>
                              {item.title === 'Inbox' && totalUnreadConversations > 0 && (
                                collapsed && !isMobile ? (
                                  <span className="absolute -top-[2px] -right-[5px] flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white">
                                    {totalUnreadConversations}
                                  </span>
                                ) : (
                                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-xs text-white">
                                    {totalUnreadConversations}
                                  </span>
                                )
                              )}
                            </div>
                          </SidebarMenuButton>,
                          item.title
                        )}
                      </SidebarMenuItem>
                    );
                  })}
              </SidebarMenu>
            </SidebarGroup>
          )}

          {showRegularNav && !isLoadingEntitlements && primaryMenuItems
            .filter(primaryItem => {
              if (HIDDEN_PRIMARY_MENU_IDS.includes(primaryItem.id)) return false;
              return isPrimaryGroupAllowed(primaryItem);
            })
            .map((primaryItem) => {
              const filteredSubItems = (hasCustomPermissions
                ? primaryItem.secondaryItems.filter((item: any) => isFeatureEnabled(item.featureKey, item.url) && isRoleAllowedForUrl(item.url))
                : filterSecondaryItems(primaryItem.secondaryItems)
              ).filter((item: any) =>
                !(item.url === '/home/team-chat' && isRoleAllowedForUrl('/home/team-management'))
              );
              const active = isGroupActive(filteredSubItems, primaryItem.id);
              const isSingleNode =
                primaryItem.id === 'data-store' ||
                primaryItem.id === 'customers' ||
                primaryItem.id === 'social-media' ||
                primaryItem.id === 'new-campaigns' ||
                primaryItem.id === 'reports' ||
                primaryItem.id === 'ai-tools' ||
                primaryItem.id === 'commerce-v2' ||
                primaryItem.id === 'appointments-v2' ||
                primaryItem.id === 'petpooja';
              const singleNodeItem = filteredSubItems[0];

              if (isSingleNode && singleNodeItem) {
                const singleActive = primaryItem.id === 'customers'
                  ? ['/home/boards/leads', '/home/boards/customers', '/home/tickets'].some((p) => isRouteActive(p))
                  : primaryItem.id === 'reports'
                    ? [
                        '/home/analytics',
                        '/home/sales-and-conversions',
                        '/home/message-reports',
                        '/home/campaign-performance',
                        '/home/ads-results',
                      ].some((p) => isRouteActive(p))
                  : primaryItem.id === 'ai-tools'
                    ? [
                        '/home/ai-agents',
                        '/home/whatsapp-ai-agent',
                        '/home/live-chat',
                        '/home/voice-agents',
                        '/home/chatbot',
                      ].some((p) => isRouteActive(p))
                  : primaryItem.id === 'new-campaigns'
                    ? ['/home/new-campaigns', '/home/new-templates'].some((p) => isRouteActive(p))
                    : isRouteActive(singleNodeItem.url);
                return (
                  <SidebarMenuItem key={primaryItem.id} className="list-none">
                    {wrapWithTooltip(
                      <SidebarMenuButton
                        isActive={singleActive}
                        className={collapsed && !isMobile ? navRailRow(singleActive) : navRow(singleActive)}
                        onClick={() => handleNavigation(singleNodeItem.url)}
                      >
                        <div className={cn("flex items-center gap-3 w-full", collapsed && !isMobile && "justify-center gap-0")}>
                          <NavIcon
                            src={primaryItem.icon}
                            active={singleActive}
                            onBrandFill={collapsed && !isMobile}
                          />
                          <span className={`${collapsed && !isMobile ? 'sr-only' : 'flex-1 text-[14.5px] font-medium'}`}>
                            {primaryItem.title}
                          </span>
                        </div>
                      </SidebarMenuButton>,
                      primaryItem.title
                    )}
                  </SidebarMenuItem>
                );
              }

              if (collapsed && !isMobile) {
                return (
                  <SidebarMenuItem key={primaryItem.id}>
                    <HoverCard openDelay={0} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <SidebarMenuButton
                          isActive={active}
                          className={navRailRow(active)}
                        >
                          <NavIcon
                            src={primaryItem.icon}
                            active={active}
                            onBrandFill
                            className="flex-shrink-0"
                          />
                          <span className="sr-only">{primaryItem.title}</span>
                        </SidebarMenuButton>
                      </HoverCardTrigger>
                      <HoverCardContent
                        side="right"
                        align="start"
                        className="w-56 ml-2 p-2 rounded-xl border shadow-lg bg-white/95 backdrop-blur-sm animate-in fade-in-0 zoom-in-95 slide-in-from-left-2"
                      >
                        <div className="px-2 py-2 text-xs border-b-[1px] font-bold text-[#08090A] uppercase tracking-wider mb-1">
                          {primaryItem.title}
                        </div>
                        {filteredSubItems.map(subItem => {
                          return (
                          <div
                            key={subItem.url}
                            onClick={() => handleNavigation(subItem.url)}
                            className={cn(
                              'my-0.5 flex cursor-pointer items-center',
                              navSubRow(isRouteActive(subItem.url)),
                              '!pl-3 !pr-3',
                            )}
                          >
                            <span aria-hidden="true" className={navSubDot(isRouteActive(subItem.url))} />
                            {subItem.logo || subItem.logoIcon ? (
                              <SubNavLogo
                                src={subItem.logo}
                                icon={subItem.logoIcon}
                                iconClassName={subItem.logoIconClassName}
                              />
                            ) : null}
                            <span className="min-w-0 flex-1 truncate">{subItem.title}</span>
                          </div>
                          );
                        })}
                      </HoverCardContent>
                    </HoverCard>
                  </SidebarMenuItem>
                );
              }

              return (
                <Collapsible
                  key={primaryItem.id}
                  asChild
                  open={openAccordion === primaryItem.id}
                  onOpenChange={(isOpen) => setOpenAccordion(isOpen ? primaryItem.id : null)}
                  className="group/collapsible list-none"
                >
                  <SidebarMenuItem className="list-none ">
                    <CollapsibleTrigger asChild >
                      <SidebarMenuButton
                        isActive={active}
                        className={navRow(active)}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <NavIcon
                            src={primaryItem.icon}
                            active={active}
                            onBrandFill={collapsed && !isMobile}
                          />
                          <span className="min-w-0 truncate text-[14.5px] font-medium">{primaryItem.title}</span>
                          {primaryItem.id === 'ads' ? (
                            <span
                              aria-hidden="true"
                              className="shrink-0 rounded-full bg-blue-100 px-1.5 py-[3px] text-[9px] font-bold uppercase leading-none tracking-wider text-blue-700"
                            >
                              Beta
                            </span>
                          ) : null}
                          <span className={cn('ml-auto', navToggle(active))}>
                            {openAccordion === primaryItem.id ? (
                              <Minus className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <Plus className="h-3.5 w-3.5 shrink-0" />
                            )}
                          </span>
                        </div>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub className="mr-0 pr-0 pl-3 ml-0 border-l-0 gap-1 py-0 list-none [&>li]:list-none">
                        {filteredSubItems.map(subItem => {
                          const hasChildSibling = filteredSubItems.some(
                            s => s.url !== subItem.url && s.url.startsWith(subItem.url + '/')
                          );
                          const subActive = isRouteActive(subItem.url, hasChildSibling);
                          return (
                            <SidebarMenuSubItem key={subItem.url}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={subActive}
                                onClick={() => {
                                  handleNavigation(subItem.url);
                                }}
                                className={cn('cursor-pointer', navSubRow(subActive))}
                              >
                                <div className="flex w-full items-center gap-2.5">
                                  <span aria-hidden="true" className={navSubDot(subActive)} />
                                  {subItem.logo || subItem.logoIcon ? (
                                    <SubNavLogo
                                      src={subItem.logo}
                                      icon={subItem.logoIcon}
                                      iconClassName={subItem.logoIconClassName}
                                    />
                                  ) : null}
                                  <span className="min-w-0 flex-1 truncate">{subItem.title}</span>
                                </div>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              );
            })}

          {showAdminNav && (
            <SidebarGroup className="p-0 pt-[10px] list-none">
              <SidebarMenu className="list-none gap-0">
                {adminNavItems.map(item => {
                  const active = isRouteActive(item.url);
                  const IconToRender = typeof item.icon === 'string' ? null : item.icon;

                  return (
                    <SidebarMenuItem key={item.title}>
                      {wrapWithTooltip(
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          className={navRow(active)}
                          onClick={() => handleNavigation(item.url)}
                        >
                          <div className={cn("flex items-center gap-3 cursor-pointer w-full", collapsed && !isMobile && "justify-center gap-0")}>
                            {typeof item.icon === 'string' ? (
                              <NavIcon
                                src={item.icon}
                                active={active}
                                onBrandFill={collapsed && !isMobile}
                              />
                            ) : (
                              IconToRender && <IconToRender className={`w-5 h-5 ${active ? 'text-white' : ''}`} />
                            )}
                            <span className={cn("truncate flex-1 text-[14.5px] font-medium", collapsed && !isMobile && "sr-only")}>{item.title}</span>
                          </div>
                        </SidebarMenuButton>,
                        item.title
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          )}

          {showAgencyNav && (
            <SidebarGroup className="p-0 pt-[10px] list-none">
              <SidebarMenu className="list-none gap-0">
                {agencyNavItems.map(item => {
                  const active = isRouteActive(item.url);

                  return (
                    <SidebarMenuItem key={item.title}>
                      {wrapWithTooltip(
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          className={navRow(active)}
                          onClick={() => handleNavigation(item.url)}
                        >
                          <div className={cn("flex items-center gap-3 cursor-pointer w-full", collapsed && !isMobile && "justify-center gap-0")}>
                            <NavIcon
                              src={item.icon}
                              active={active}
                              onBrandFill={collapsed && !isMobile}
                            />
                            <span className={cn("truncate flex-1 text-[14.5px] font-medium", collapsed && !isMobile && "sr-only")}>{item.title}</span>
                          </div>
                        </SidebarMenuButton>,
                        item.title
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          )}
        </SidebarContent>
        {isCardShell && showRegularNav && (isMobile || !collapsed) && (
          <SidebarFooter className="px-3 pb-3 pt-2 bg-transparent">
            <PlanCreditsBar variant="card" />
          </SidebarFooter>
        )}

        {isMobile && !showRegularNav && (
          <SidebarFooter className={cn(
            'px-3 py-3 flex flex-col gap-2',
            isCardShell ? 'bg-transparent' : 'border-t border-slate-100 bg-white'
          )}>
            <HeaderBusinessSelector
              selectedAccountId={businessAccountContext?.selectedAccountId || ''}
              onAccountChange={(id) => businessAccountContext?.setSelectedAccountId?.(id)}
              alwaysShowDetails
            />
          </SidebarFooter>
        )}
      </Sidebar>
    </>
  );
};
